import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, switchMap, tap } from 'rxjs/operators';
import * as jwtDecode from 'jwt-decode';
import * as moment from 'moment';
import 'rxjs/add/operator/delay';

import { environment } from '../../../environments/environment';
import { of, EMPTY, Observable, BehaviorSubject } from 'rxjs';
import { User } from '../../models/user';
import { Permission } from '../../models/permission';

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'user-key': environment.apiKey,
        'X-Tenant-ID': environment.clientID
    })
};

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    private endpoint: string;

    constructor(private http: HttpClient,
        @Inject('LOCALSTORAGE') private localStorage: Storage) {

        this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();
        this.endpoint = environment.apiUrl;
    }

    login(email: string, password: string) {
        return this.http.post<any>(this.endpoint + `/users/authenticate`, { email, password }, httpOptions)
            .pipe(switchMap(response => {

                if (response && response.token != null) {
                    const decoded: any = jwtDecode(response.token);

                    const user: User = {
                        ...response,
                        token: response.token,
                        role: decoded.role || response.Role
                    };

                    this.localStorage.setItem('currentUser', JSON.stringify(user));
                    this.currentUserSubject.next(user);

                    return this.getUserPermissions(user.id).pipe(
                        tap(perms => {
                            const permNames = perms.map(p => p.name);
                            this.setPermissions(permNames);

                            user.permissions = permNames;
                            
                            this.localStorage.setItem('currentUser', JSON.stringify(user));
                            
                            this.currentUserSubject.next(user);
                        }),
                        map(() => user)
                    );
                }

                return of(response);
            })
        );
    }

    getUserPermissions(userId) {
        return this.http.get<Permission[]>(this.endpoint + `/users/${userId}/permissions`, httpOptions);
    }

    getPermissions(): string[] {
        return JSON.parse(this.localStorage.getItem('permissions') || '[]');
    }

    refreshPermissions(): Observable<Permission[]> {
        const user = this.getCurrentUser();
        if (!user) return of([]);

        return this.getUserPermissions(user.id).pipe(
            tap(perms => {
                const permNames = perms.map(p => p.name);
                this.setPermissions(permNames);
                user.permissions = permNames;
                this.localStorage.setItem('currentUser', JSON.stringify(user));
                this.currentUserSubject.next(user);
            })
        );
    }

    logout() {
        // remove user from local storage to log user out
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
    }

    getCurrentUser(): User {
        return this.currentUserSubject.value;
    }

    getToken(): string | null {
        return this.getCurrentUser()?.token || null;
    }

    passwordResetRequest(email: string) {
        return of(true).delay(1000);
    }

    changePassword(email: string, currentPwd: string, newPwd: string) {
        return of(true).delay(1000);
    }

    passwordReset(email: string, token: string, password: string, confirmPassword: string): any {
        return of(true).delay(1000);
    }

    hasRole(role: string): boolean {
        return this.getCurrentUser()?.role.name === role;
    }

    hasPermission(permission: string): boolean {
        const permissions = this.getPermissions();
        return permissions.includes(permission);
    }

    private setPermissions(perms: string[]) {
        this.localStorage.setItem('permissions', JSON.stringify(perms));
    }
}